import { Fragment, Component } from 'react';
import debounce from 'react-utils/debounce';
import shallowCompare from 'react-addons-shallow-compare';
import { omit } from 'underscore';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import ImagePreview from 'ContentComponents/cmv2/fields/ImagePreview';
import FileBase from 'ContentComponents/cmv2/components/FileBase';
import ImageSizing from 'ContentComponents/cmv2/media/ImageSizing';
import LoadingSelect from 'ContentComponents/cmv2/media/LoadingSelect';
import { FileTypes } from 'file-manager-components/constants';
import { WARNING_FILE_SIZE, WARNING_FILE_TYPE } from 'ContentUtils/constants/ImagePerformanceTypes';
import ImageLoadingTypes from 'ContentUtils/constants/ImageLoadingTypes';
import ImageSizeTypes from 'ContentUtils/constants/ImageSizeTypes';
import { getMaxDimensionsBasedOnImageSize } from 'ContentUtils/helpers/ImageHelpers';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.image.${key}`;
const buttonI18nKey = key => `ContentComponents.buttons.${key}`;
const HUBL_VAR = /^{{\s*([a-zA-Z0-9_.]+)\s*}}$/;
const SpanNoBreak = styled.span.withConfig({
  displayName: "ImageBase__SpanNoBreak",
  componentId: "sc-1lalboz-0"
})(["white-space:nowrap;"]);
const HighlightInput = styled.div.withConfig({
  displayName: "ImageBase__HighlightInput",
  componentId: "sc-1lalboz-1"
})(({
  useXrayHighlight,
  theme
}) => {
  return useXrayHighlight && {
    border: `2px solid ${theme.color['fill-accent-purple-default']}`,
    borderRadius: '4px'
  };
});
const WARNING_TYPES = {
  FILE_TYPE: 'fileTypeWarning',
  FILE_SIZE_1MB: 'fileSizeWarning1MB',
  FILE_SIZE_50MB: 'fileSizeWarning50MB',
  REDUCED_DIMENSIONS: 'automaticallyReducedMaxDimension',
  IMAGE_NOT_LOADED: 'imageNotLoaded'
};
class ImageFieldBase extends Component {
  constructor(props) {
    super(props);
    this.handleImageSelect = this.handleImageSelect.bind(this);
    this.onAltTextChange = this.onAltTextChange.bind(this);
    this.trackAltTextChange = this.trackAltTextChange.bind(this);
    this.onClearImage = this.onClearImage.bind(this);
    this.onFileManagerOpen = this.onFileManagerOpen.bind(this);
    this.onLazyLoadingChange = this.onLazyLoadingChange.bind(this);
    this.setAspectRatio = this.setAspectRatio.bind(this);
    this.setImageNaturalDimensions = this.setImageNaturalDimensions.bind(this);
    this.onCloseWarning = this.onCloseWarning.bind(this);
    this.resetFileAttributeWarnings = this.resetFileAttributeWarnings.bind(this);
    this.resetFileSizeWarnings = this.resetFileSizeWarnings.bind(this);
    this.resetFileInfoNotices = this.resetFileInfoNotices.bind(this);
    this.debouncedTrackAltTextChange = debounce(this.trackAltTextChange, 250);
    this.naturalImg = null;
    this.naturalImgData = null;
    this.state = {
      aspectRatio: null,
      fileAttributeWarnings: [],
      fileSizeWarnings: [],
      fileInfoNotices: [],
      fileLoadingErrors: []
    };
  }
  componentDidMount() {
    const {
      value = {}
    } = this.props;
    if (value && value.src) {
      if (value.height && value.width) {
        this.setAspectRatio(value);
      } else {
        this.setImageNaturalDimensions(value);
      }
    }
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentDidUpdate(prevProps) {
    const {
      value = {}
    } = this.props;
    if (!this.state.aspectRatio && prevProps.value === null && value) {
      this.setAspectRatio(value);
    }
  }
  componentWillUnmount() {
    const {
      onFieldChange
    } = this.props;
    // If the image is in the proccess of fetching its natural dimensions but
    // should update once it's finished, short circuit and immediately call onFieldChange
    if (this.naturalImg) this.naturalImg.onload = null;
    if (this.naturalImgData) onFieldChange(this.naturalImgData);
  }
  get fieldKey() {
    const {
      fieldKey = []
    } = this.props;
    return fieldKey && fieldKey.join('.');
  }
  get responsiveEnabled() {
    const {
      forceHideResponsive,
      responsive
    } = this.props;
    return !forceHideResponsive && responsive;
  }
  setAspectRatio(value) {
    if (!value) return;
    const {
      width,
      height
    } = value;
    if (width && height) {
      this.setState({
        aspectRatio: width / height
      });
    }
  }
  buildManualFetchImageSrc(imageSrc) {
    const [start, ...rest] = imageSrc.split('?');
    const params = new URLSearchParams(rest.join(''));
    params.append('manualFetch', `${Date.now()}`);
    return `${start}?${params.toString()}`;
  }
  setImageNaturalDimensions(image, shouldUpdate = false) {
    const {
      onFieldChange
    } = this.props;
    const newImage = Object.assign({}, image);
    this.naturalImg = new Image();
    this.naturalImgData = shouldUpdate ? newImage : null;

    // Make a custom URL sp that it is easlier  to grab the exact PerformanceElementTiming of later
    const manualImageFetchSrc = this.buildManualFetchImageSrc(image.src);

    // When the image loads, set natural height and width in
    // state to use as default values for the dimensions
    this.naturalImg.onload = () => {
      const {
        naturalHeight,
        naturalWidth
      } = this.naturalImg;
      newImage.width = naturalWidth;
      newImage.height = naturalHeight;
      if (this.responsiveEnabled) {
        const {
          areaReductionRatio,
          maxHeight,
          maxWidth
        } = getMaxDimensionsBasedOnImageSize(naturalWidth, naturalHeight);
        newImage.max_width = maxWidth;
        newImage.max_height = maxHeight;
        if (areaReductionRatio > 0) {
          newImage.size_type = ImageSizeTypes.AUTO_CUSTOM_MAX;
        }
      }
      this.naturalImg = null;
      this.naturalImgData = null;
      this.setAspectRatio(newImage);
      if (shouldUpdate) onFieldChange(newImage);

      // TODO use `window.performance.getEntriesByName(manualImageFetchSrc)[0]` to get at decodedBodySize of fetched image
      // and then setImageSizeWarningsAndNotices
      // https://git.hubteam.com/HubSpot/ContentPerf/issues/226 and https://git.hubteam.com/HubSpot/ContentUILib/pull/3596/files/6108021761ba469679b5edc4252eda0c780272aa#diff-c4f934daaaf1f570525f3a36d7cff003536275899c5f0206c86f859f2d074262
    };

    // If the image load fails, call onFieldChange
    this.naturalImg.onerror = () => {
      this.naturalImg = null;
      this.naturalImgData = null;
      if (shouldUpdate) onFieldChange(newImage);
      this.setImageLoadingErrors();
    };
    this.naturalImg.src = manualImageFetchSrc;
  }
  resetFileAttributeWarnings() {
    this.setState({
      fileAttributeWarnings: []
    });
  }
  resetFileSizeWarnings() {
    this.setState({
      fileSizeWarnings: []
    });
  }
  resetFileInfoNotices() {
    this.setState({
      fileInfoNotices: []
    });
  }
  resetFileLoadingErrors() {
    this.setState({
      fileLoadingErrors: []
    });
  }
  setImageAttributeWarnings(response) {
    this.resetFileAttributeWarnings();
    const fileAttributeWarnings = [];
    const fileExtension = response.extension;
    if (fileExtension === WARNING_FILE_TYPE) {
      fileAttributeWarnings.push(WARNING_TYPES.FILE_TYPE);
    }
    this.setState({
      fileAttributeWarnings
    });
  }
  setImageSizeWarningsAndNotices(fileSize, areaReductionRatio = 0) {
    this.resetFileSizeWarnings();
    this.resetFileInfoNotices();
    const fileSizeWarnings = [];
    const fileInfoNotices = [];

    // If we automatically constrict the max dimension, estimate size savings
    // based on the reduced area
    if (areaReductionRatio > 0) {
      fileSize = fileSize * areaReductionRatio;
      fileInfoNotices.push(WARNING_TYPES.REDUCED_DIMENSIONS);
    }
    if (fileSize >= WARNING_FILE_SIZE.FIFTY_MB) {
      fileSizeWarnings.push(WARNING_TYPES.FILE_SIZE_50MB);
    } else if (fileSize >= WARNING_FILE_SIZE.ONE_MB) {
      fileSizeWarnings.push(WARNING_TYPES.FILE_SIZE_1MB);
    }
    this.setState({
      fileSizeWarnings,
      fileInfoNotices
    });
  }
  setImageLoadingErrors() {
    this.resetFileLoadingErrors();
    this.setState({
      fileLoadingErrors: [WARNING_TYPES.IMAGE_NOT_LOADED]
    });
  }
  handleImageSelect(response) {
    this.resetFileLoadingErrors();
    const {
      value = {}
    } = this.props;
    const fileObject = response.toJS ? response.toJS() : response;
    let areaReductionRatio;
    let alt = fileObject.name || fileObject.title;
    if (fileObject.meta && fileObject.meta.title) {
      alt = fileObject.meta.title;
    }
    const image = Object.assign({}, omit(value, ['max_width', 'max_height']), {
      alt,
      src: fileObject.default_hosting_url,
      width: fileObject.width,
      height: fileObject.height
    });
    if (this.responsiveEnabled) {
      const {
        areaReductionRatio: newAreaReductionRatio,
        maxHeight,
        maxWidth
      } = getMaxDimensionsBasedOnImageSize(fileObject.width, fileObject.height);
      image.max_width = maxWidth;
      image.max_height = maxHeight;
      areaReductionRatio = newAreaReductionRatio;
      if (areaReductionRatio > 0) {
        image.size_type = ImageSizeTypes.AUTO_CUSTOM_MAX;
      }
    }
    if (image.height && image.width) {
      this.setAspectRatio(image);
      this.props.onFieldChange(image);
      this.setImageSizeWarningsAndNotices(response.size, areaReductionRatio);
    } else {
      this.setImageNaturalDimensions(image, true);
    }
    this.setImageAttributeWarnings(fileObject);
  }
  onClearImage() {
    this.resetFileAttributeWarnings();
    this.resetFileSizeWarnings();
    this.resetFileInfoNotices();
    this.resetFileLoadingErrors();
    this.props.onFieldChange({
      src: ''
    });
  }
  onFileManagerOpen(__handleSelect, options = {}) {
    const {
      onFileManagerOpen
    } = this.props;
    onFileManagerOpen(this.handleImageSelect, options);
  }
  onLazyLoadingChange(loadingType) {
    const {
      value = {},
      onFieldChange
    } = this.props;
    onFieldChange(Object.assign({}, value, {
      loading: loadingType
    }));
  }
  trackAltTextChange() {
    const {
      trackFieldAction
    } = this.props;
    if (trackFieldAction) {
      trackFieldAction('fileManagerManageFiles', {
        action: 'update alt text',
        fileType: FileTypes.IMG
      });
    }
  }
  onAltTextChange(e) {
    const {
      value = {},
      onFieldChange
    } = this.props;
    const newAltText = e.target.value;
    onFieldChange(Object.assign({}, value, {
      alt: newAltText
    }));
    this.debouncedTrackAltTextChange();
  }
  onCloseWarning(warningType) {
    this.setState(prevState => ({
      fileAttributeWarnings: prevState.fileAttributeWarnings.filter(el => el !== warningType),
      fileSizeWarnings: prevState.fileSizeWarnings.filter(el => el !== warningType),
      fileInfoNotices: prevState.fileInfoNotices.filter(el => el !== warningType),
      fileLoadingErrors: prevState.fileLoadingErrors.filter(el => el !== warningType)
    }));
  }
  renderLazyLoadingSelect() {
    const {
      value = {},
      readOnly,
      useOnDark
    } = this.props;
    return /*#__PURE__*/_jsx(LoadingSelect, {
      onChange: this.onLazyLoadingChange,
      readOnly: readOnly,
      useOnDark: useOnDark,
      value: value.loading || ImageLoadingTypes.DISABLED
    });
  }
  renderAltTextInput() {
    const {
      highlightedField = [],
      readOnly,
      showAlt = true,
      useOnDark,
      value = {}
    } = this.props;
    if (!showAlt) return null;
    const useXrayHighlight = highlightedField.length === 1 && highlightedField[0] === 'alt';
    return /*#__PURE__*/_jsx(UIFormControl, {
      className: "m-top-4",
      label: text(i18nKey('altTextLabel')),
      children: /*#__PURE__*/_jsx(HighlightInput, {
        useXrayHighlight: useXrayHighlight,
        children: /*#__PURE__*/_jsx(TextInput, {
          "data-selenium": `${this.fieldKey}:alt`,
          "aria-label": text(i18nKey('altTextLabel')),
          onChange: this.onAltTextChange,
          readOnly: readOnly,
          use: useOnDark ? 'on-dark' : 'default',
          value: value && value.alt
        })
      })
    });
  }
  renderTextFieldForImage(src) {
    if (HUBL_VAR.test(src)) {
      return /*#__PURE__*/_jsx(UITextInput, {
        value: src,
        readOnly: true
      }); // read-only styles override use on-dark ones
    }
    return null;
  }
  renderWarnings() {
    const {
      fileAttributeWarnings,
      fileSizeWarnings,
      fileInfoNotices,
      fileLoadingErrors
    } = this.state;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [fileAttributeWarnings.length > 0 && fileAttributeWarnings.map(warningType => this.renderUIAlert('warning', warningType)), fileSizeWarnings.length > 0 && fileSizeWarnings.map(warningType => this.renderUIAlert('warning', warningType)), fileInfoNotices.length > 0 && fileInfoNotices.map(noticeType => this.renderUIAlert('info', noticeType)), fileLoadingErrors.length > 0 && fileLoadingErrors.map(errorType => this.renderUIAlert('danger', errorType))]
    });
  }
  renderUIAlert(alertType, warningType) {
    const {
      isInEmail,
      value
    } = this.props;
    const maybeAddEmailTextKey = isInEmail ? '.email' : '';
    const warningTypeJSX = warningType === WARNING_TYPES.IMAGE_NOT_LOADED && `${warningType}_jsx`;
    const titleKey = i18nKey(`warnings.${warningType}.title`);
    const hasTitle = isValidI18nKey(titleKey);
    return /*#__PURE__*/_jsx(UIAlert, {
      use: "inline",
      titleText: hasTitle && /*#__PURE__*/_jsx(FormattedMessage, {
        message: titleKey
      }),
      className: "m-bottom-2",
      closeable: true,
      onClose: () => this.onCloseWarning(warningType),
      type: alertType,
      children: warningTypeJSX ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey(`warnings.${warningTypeJSX}.text`),
        elements: {
          UILink,
          SpanNoBreak
        },
        options: {
          src: value === null || value === void 0 ? void 0 : value.src
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`warnings.${warningType}${maybeAddEmailTextKey}.text`)
      })
    }, warningType);
  }
  render() {
    const {
      contentAssistanceEnabled,
      fieldKey = [],
      forceShowLoading = false,
      help_text: helpText,
      inline_help_text: inlineHelpText,
      isInEmail,
      label = '',
      maxContainerWidth,
      onFieldChange,
      readOnly,
      required,
      resizable = true,
      show_loading: showLoading = false,
      trackFieldAction,
      uploadedFileAccess,
      useOnDark,
      value = {}
    } = this.props;
    const hasValue = value && value.src;
    const showLoadingOptions = showLoading && hasValue && !isInEmail || forceShowLoading;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderWarnings(), /*#__PURE__*/_jsx(FileBase, {
        browseText: unescapedText(i18nKey('browseImages')),
        contentAssistanceEnabled: contentAssistanceEnabled,
        fieldKey: fieldKey,
        fileType: FileTypes.IMG,
        handleFileSelect: this.handleImageSelect,
        hasValue: !!hasValue,
        helpText: helpText,
        inlineHelpText: inlineHelpText,
        label: label,
        onClearFile: this.onClearImage,
        onFileManagerOpen: this.onFileManagerOpen,
        readOnly: readOnly,
        required: required,
        trackFieldAction: trackFieldAction,
        uploadedFileAccess: uploadedFileAccess,
        uploadText: unescapedText(i18nKey('uploadImage')),
        children: hasValue && /*#__PURE__*/_jsxs("div", {
          children: [this.renderTextFieldForImage(value.src) || /*#__PURE__*/_jsx(ImagePreview, {
            alt: value.alt,
            src: value.src,
            onHoverlayClick: this.onFileManagerOpen,
            hoverlayLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: buttonI18nKey('replace')
            }),
            readOnly: readOnly
          }), this.renderAltTextInput(), /*#__PURE__*/_jsx(ImageSizing, {
            aspectRatio: this.state.aspectRatio,
            fieldKey: this.fieldKey,
            onFieldChange: onFieldChange,
            readOnly: readOnly,
            maxContainerWidth: maxContainerWidth,
            resizable: resizable,
            responsiveEnabled: this.responsiveEnabled,
            useOnDark: useOnDark,
            value: value
          })]
        })
      }), showLoadingOptions && !isInEmail && this.renderLazyLoadingSelect()]
    });
  }
}
export default ImageFieldBase;